import { useEffect, useState } from 'react';
import { api } from '../lib/api.ts';
import { formatStamp, usePrefs } from '../lib/prefs.tsx';
import { formatDistance } from '../lib/maintenance.ts';
import { Term } from './Term.tsx';

/**
 * The fleet at a glance.
 *
 * The question this answers is the one the app could not: which cars in the
 * building need something. Previously that meant opening five vehicles in turn.
 *
 * Everything shown is stored locally, so this stays a read rather than a
 * calculation. Open recalls need a network lookup per vehicle and maintenance
 * needs the interval logic run per vehicle, so both are left to the vehicle
 * view, which does the authoritative check.
 */
export function FleetPanel({
  onSelect, refreshKey, selected,
}: {
  onSelect?: (vin: string) => void;
  refreshKey: number;
  selected?: string | null;
}) {
  const { units, dateFormat } = usePrefs();
  const [query, setQuery] = useState('');
  const [fleet, setFleet] = useState<Awaited<ReturnType<typeof api.fleet>> | null>(null);

  useEffect(() => {
    api.fleet().then(setFleet).catch(() => setFleet(null));
  }, [refreshKey]);

  if (!fleet || fleet.length === 0) return null;

  const withCodes = fleet.filter((v) => v.dtcCount > 0);
  const staleOdo = fleet.filter((v) => v.odometerKm === null);

  // Matches anything printed on the row plus the raw VIN, so a partial plate, a
  // make or the last few VIN characters all find the vehicle. The tallies stay
  // whole-fleet, because they describe the fleet rather than the search.
  const needle = query.trim().toLowerCase();
  const shown = needle
    ? fleet.filter((v) =>
        [v.label, v.make, v.model, v.modelYear, v.vin].filter(Boolean).join(' ').toLowerCase().includes(needle),
      )
    : fleet;

  return (
    <section className="stack fleet">
      {/* "Fleet" is shop language. A customer has their own car, or two, and
          providers are only ever set on a customer's rows - so the data says
          which reader this is without a prop being threaded down. */}
      <span className="eyebrow">{fleet.some((v) => v.providers) ? 'Your vehicles' : 'Fleet'}</span>

      <div className="fleet-tally">
        <Tally n={fleet.length} label="vehicles" />
        <Tally n={withCodes.length} label="with codes" tone={withCodes.length ? 'crit' : undefined} />
        <Tally n={staleOdo.length} label="no mileage" tone={staleOdo.length ? 'warn' : undefined} />
      </div>

      {fleet.length > 6 && (
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search make, model, year or VIN"
          aria-label="Filter vehicles"
        />
      )}

      {shown.length === 0 && <p className="dim small">Nothing matches “{query}”.</p>}

      <div className="stack" style={{ gap: 4 }}>
        {shown.map((v) => (
          <button
            key={v.vin}
            className="fleet-row"
            aria-current={selected === v.vin ? 'true' : undefined}
            onClick={() => onSelect?.(v.vin)}
            title={`${v.scanCount} scan${v.scanCount === 1 ? '' : 's'}`}
          >
            <span className="fleet-name">
              {v.label || [v.modelYear, v.make, v.model].filter(Boolean).join(' ') || v.vin}
            </span>
            {/* Who is holding it. Only a customer sees this, and only they can
                have more than one: a car stored at one place and serviced at
                another is still one car, and naming the providers is how that
                stays true without listing it twice. */}
            {v.providers && v.providers.length > 0 && (
              <span className="fleet-providers small dim">{v.providers.join(' · ')}</span>
            )}
            <span className="fleet-flags">
              {/* The rows sort by this, so showing it makes the ordering
                  legible rather than something you have to take on trust. */}
              <span className="fleet-when mono">
                {v.lastScanAt ? formatStamp(v.lastScanAt, dateFormat) : 'never scanned'}
              </span>
              {v.dtcCount > 0 && (
                <span className="pill crit" title={`${v.dtcCount} diagnostic trouble code(s) on the last scan`}>
                  {v.dtcCount}
                </span>
              )}
              {v.milOn && (
                <span className="pill warn">
                  <Term of="MIL">MIL</Term>
                </span>
              )}
              {/* A code count says something is wrong. This says there is
                  something to do about it, which is the more useful signal when
                  deciding which car to pick up next. */}
              {v.partsAvailable && (
                <span className="pill accent" title="The last scan has codes with parts listed">
                  parts
                </span>
              )}
              {v.odometerKm !== null ? (
                <span className="fleet-odo mono">{formatDistance(v.odometerKm, units)}</span>
              ) : (
                <span className="fleet-odo mono dim">no mileage</span>
              )}
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}

function Tally({ n, label, tone }: { n: number; label: string; tone?: 'crit' | 'warn' }) {
  return (
    <div className={`tally ${tone ?? ''}`}>
      <span className="tally-n">{n}</span>
      <span className="tally-label">{label}</span>
    </div>
  );
}
